import { useState, useEffect } from "react";
import { cn } from "~/lib/utils";
import { timezones, formatClockWithTz } from "~/lib/timezone";

import { ErrorProps, FormError } from ".";
import {
  Button,
  Icon,
  Input,
  Label,
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
  Separator,
  Slider,
} from "../ui";

interface Props {
  label: string;
  required?: boolean;
  errors?: ErrorProps;
  value?: string | undefined;
  defaultValue?: string | undefined;
  onChange: (option: string | undefined) => void;
  placeholder?: string;
}

export default function FormTimeInput({
  label,
  errors,
  value,
  onChange,
  placeholder,
  defaultValue,
}: Props) {
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [light, setLight] = useState(true);
  const [selectedTimezone, setSelectedTimezone] = useState("GMT");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const val = value || defaultValue;
    if (val) {
      const match = val.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?(?:\s+(.+))?$/i);
      if (match) {
        let h = parseInt(match[1], 10);
        const m = parseInt(match[2], 10);
        const ampm = match[3];
        const tz = match[4] || "GMT";

        let isLight = true;
        if (ampm) {
          isLight = ampm.toUpperCase() === "AM";
        } else {
          // 24-hour parsing fallback
          if (h >= 12) {
            isLight = false;
            if (h > 12) h -= 12;
          } else {
            isLight = true;
            if (h === 0) h = 12;
          }
        }

        setHours(h);
        setMinutes(m);
        setLight(isLight);
        setSelectedTimezone(tz);
      }
    }
  }, [value, defaultValue]);

  const handleHoursChange = (values: number[]) => {
    setHours(values[0]);
    onChange(formatClockWithTz(values[0], minutes, light, selectedTimezone));
  };

  const handleMinutesChange = (values: number[]) => {
    setMinutes(values[0]);
    onChange(formatClockWithTz(hours, values[0], light, selectedTimezone));
  };

  const handleLightChange = (light: boolean) => {
    setLight(light);
    onChange(formatClockWithTz(hours, minutes, light, selectedTimezone));
  };

  const handleTimezoneChange = (tzId: string) => {
    setSelectedTimezone(tzId);
    onChange(formatClockWithTz(hours, minutes, light, tzId));
  };

  const resetTimeSelection = () => {
    setMinutes(0);
    setHours(0);
    setSelectedTimezone("GMT");
    onChange(formatClockWithTz(0, 0, light, "GMT"));
  };

  const filteredTimezones = timezones.filter(
    (tz) =>
      tz.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tz.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tz.displayOffset.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <Popover>
      <PopoverTrigger className="w-full p-0 py-0">
        <MomentInput
          value={value}
          defaultValue={defaultValue}
          onChange={onChange}
          label={label}
          errors={errors}
          placeholder={placeholder}
        />
      </PopoverTrigger>
      <PopoverContent
        onWheel={(event) => event.stopPropagation()}
        align="start"
        className="relative w-80 space-y-4 overflow-visible px-6 py-5"
      >
        <div className="flex items-center justify-between gap-4">
          <div className="font-Regular text-xl">
            <span>{hours.toString().padStart(2, "0")}</span>
            <span>:</span>
            <span>{minutes.toString().padStart(2, "0")}</span>
            <span>{light ? "AM" : "PM"}</span>
          </div>
          <div>
            <div className="rounded-lg border border-neutral-200 p-0.5">
              <ul className="flex gap-x-0.5">
                <li
                  className={cn(
                    "rounded-lg px-2 hover:cursor-pointer",
                    !light && "bg-white text-black",
                    light && "bg-primary-500 text-white",
                  )}
                  onClick={() => handleLightChange(true)}
                >
                  <span className="text-sm">AM</span>
                </li>
                <li
                  className={cn(
                    "rounded-lg px-2 hover:cursor-pointer",
                    light && "bg-white text-black",
                    !light && "bg-primary-500 text-white",
                  )}
                  onClick={() => handleLightChange(false)}
                >
                  <span className="text-sm">PM</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <Separator className="bg-neutral-200" />
        <div className="space-y-3">
          <Label className="text-sm text-black">Hours:</Label>
          <Slider
            max={12}
            step={1}
            value={[hours]}
            onValueChange={handleHoursChange}
          />
          <div className="flex justify-between text-neutral-500">
            <span>00</span>
            <span>06</span>
            <span>12</span>
          </div>
        </div>
        <div className="space-y-3 pt-2">
          <Label className="text-sm text-black">Minutes:</Label>
          <Slider
            max={59}
            step={1}
            value={[minutes]}
            onValueChange={handleMinutesChange}
          />
          <div className="flex justify-between text-neutral-500">
            <span>00</span>
            <span>30</span>
            <span>59</span>
          </div>
        </div>
        <div>
          <Separator className="bg-neutral-200" />
          <div className="flex items-center justify-between gap-3 pt-4">
            {/* Timezone Selector (Dropdown Trigger) */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center justify-between gap-1 rounded-lg border border-neutral-200 bg-white px-2.5 py-1.5 text-xs w-28 hover:bg-neutral-50 transition-colors shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 font-Medium text-neutral-700 h-9"
              >
                <div className="flex items-center gap-1.5 truncate">
                  <Icon
                    name="Globe"
                    className="h-3.5 w-3.5 text-neutral-400 shrink-0"
                  />
                  <span className="truncate">{selectedTimezone}</span>
                </div>
                <Icon
                  name="ChevronDown"
                  className="h-3 w-3 text-neutral-400 shrink-0"
                />
              </button>

              {dropdownOpen && (
                <div
                  className="absolute bottom-full mb-2 left-0 z-50 w-68 bg-white border border-neutral-200 rounded-lg shadow-lg p-2 space-y-1.5"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="relative">
                    <Input
                      placeholder="Search timezone..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="h-8 pr-7 text-xs"
                      autoFocus
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => setSearchQuery("")}
                        className="absolute right-2 top-2 text-neutral-400 hover:text-neutral-600"
                      >
                        <Icon name="X" className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                  <div className="max-h-40 overflow-y-auto pr-1 space-y-0.5 scrollbar-thin">
                    {filteredTimezones.length === 0 ? (
                      <div className="text-xs text-neutral-500 py-3 text-center">
                        No timezones found
                      </div>
                    ) : (
                      filteredTimezones.map((tz) => (
                        <button
                          key={tz.id}
                          type="button"
                          onClick={() => {
                            handleTimezoneChange(tz.id);
                            setDropdownOpen(false);
                            setSearchQuery("");
                          }}
                          className={cn(
                            "w-full text-left px-2.5 py-1.5 text-xs rounded transition-colors hover:bg-neutral-100 flex items-center justify-between",
                            selectedTimezone === tz.id &&
                              "bg-primary-500/10 text-primary-600 font-Medium",
                          )}
                        >
                          <span className="truncate">{tz.label}</span>
                          {selectedTimezone === tz.id && (
                            <Icon
                              name="Check"
                              className="h-3 w-3 text-primary-600 shrink-0"
                            />
                          )}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-2 shrink-0">
              <PopoverClose>
                <Button
                  onClick={() => {
                    resetTimeSelection();
                    setDropdownOpen(false);
                  }}
                  variant="outline"
                  className="h-9 px-3"
                >
                  Cancel
                </Button>
              </PopoverClose>
              <PopoverClose>
                <Button className="h-9 px-4">Apply</Button>
              </PopoverClose>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

const MomentInput = ({
  label,
  required,
  errors,
  value,
  onChange,
  placeholder,
  defaultValue,
}: Props) => (
  <div>
    <div className="space-y-1 text-left capitalize">
      <div
        className={cn("text-sm font-medium", {
          "text-danger-500": errors?.error,
        })}
      >
        {label} {required && <span className="text-danger-400">*</span>}
      </div>

      <div>
        <Input
          defaultValue={defaultValue}
          readOnly={true}
          value={value}
          suffix={"Clock"}
          suffixClass={"text-neutral-400"}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={cn("", {
            "border-danger-500": errors?.error,
          })}
        />
      </div>
      <FormError error={errors?.error} message={errors?.message} />
    </div>
  </div>
);
